import type {PerformanceDescendantWithId} from '@domain/kpis'
import {CollapsableRow, Column} from '@hconnect/uikit/src/lib2'
import {TableCell} from '@mui/material'
import React, {useCallback, useMemo} from 'react'

const AcVsPlTableCell = ({children, 'data-test-id': dataTestId}) => {
  return (
    <TableCell
      component="div"
      sx={{
        borderBottom: 'none',
        pl: 0
      }}
      data-test-id={dataTestId}
    >
      {children}
    </TableCell>
  )
}

type AcVsPlTableRowProps<T> = {
  item: PerformanceDescendantWithId<T>
  columns: Column<T>[]
  onLocationClick: (item: PerformanceDescendantWithId<T>) => void
  'data-test-id': string
}

export function AcVsPlTableRow<T>({
  item,
  columns,
  onLocationClick,
  'data-test-id': dataTestId
}: AcVsPlTableRowProps<T>) {
  // Region rows only expand/collapse their countries
  const getItemProps = useCallback(
    (item: PerformanceDescendantWithId<T>) =>
      item.locationData?.type === 'region' ? {} : {onClick: onLocationClick},
    [onLocationClick]
  )

  const filteredItem = useMemo(() => {
    const filteredItem = {...item}

    if (item.locationData?.type === 'area') {
      filteredItem.subItems = []
    } else if (item.locationData?.type === 'region') {
      filteredItem.subItems?.forEach((leaf) => {
        leaf.subItems = []
      })
    } else if (item.locationData?.type === 'country') {
      filteredItem.subItems = []
    }
    return filteredItem
  }, [item])

  const renderCell = useCallback(
    (itm: PerformanceDescendantWithId<T>, _: number, columnKey: string) => {
      const columnConfig = columns.find((column) => column.key === columnKey)
      return (
        <AcVsPlTableCell data-test-id={`${dataTestId}-${columnKey}`}>
          {columnConfig?.customTemplate?.(itm) || columnConfig?.key}
        </AcVsPlTableCell>
      )
    },
    [columns, dataTestId]
  )

  return (
    <CollapsableRow<PerformanceDescendantWithId<T>>
      key={filteredItem.id}
      item={filteredItem}
      columnKeys={columns.map((column) => column.key)}
      renderCell={renderCell}
      itemPropsFn={getItemProps}
      collapseToggleTestIdGenerator={(id) => `${dataTestId}-collapse-button-${id}`}
    />
  )
}
